import { ReadonlyVec3, vec3 } from 'gl-matrix';
import { Grid } from '../../engine/data/common/Grid';
import { ChangeTracker } from '../changes/ChangeTracker';
import { ImageSnapshot, TrackableObject } from '../changes/DomainChange';
import { ImageId, newImageId } from '../changes/ids';
import { GenerationModelDetails } from '../dicom/dicomBase';
import {
    DicomImageSlice,
    earlierDicomDateTime,
} from '../dicom/dicomImageSlice';
import {
    BurnedInAnnotationStatus,
    aggregateBurnedInAnnotation,
    parseBurnedInAnnotation,
} from '../dicom/burnedInAnnotation';
import { DicomSeries, Series } from '../dicom/series';
import {
    WindowLevel,
    WindowingPreset,
    computeAutoWindowLevelForCT,
    computeAutoWindowLevelFromHistogram,
    fullRangeWindowLevel,
    MR_AUTO_WL_ALGORITHMS,
    windowLevelOnBounds,
} from './windowLevel';
import { Modality } from '../dicom/modality';
import { DomainValidationError } from '../errors/DomainError';
import type { EventSource } from '../events/EventTypes';
import {
    classifySuvQuantification,
    PetSeriesMetadata,
    SuvQuantification,
} from './petSuv';
import {
    DerivedValue,
    DerivedValueByKey,
    type ResettableDerivation,
} from '~/utils/derivedValue';
import { ImageSeriesManifest } from './imageSeriesManifest';
import { ResamplingOrigin, resamplingRefusal } from './resamplingOrigin';
import { SliceGeometryInventory } from './sliceGeometry';
import {
    Image3DOptions,
    InterpolationMethod,
    SliceInfo,
    findNearestSlices,
    getMaxConsecutiveGap,
    interpolateSlice,
} from './sliceInterpolation';

/**
 * Sampled intensity histogram cropped to a clinically-relevant percentile
 * range. `bins[i]` is the bin height for the i-th equal-width bin spanning
 * `[minValue, maxValue]`. Values are integer voxel counts when the source
 * resolution is finer than the display, but become fractional weights when
 * a single source bin is spread across multiple display bins (low dynamic
 * range data); in either case bin heights remain proportional and sum to
 * the total sampled voxels inside `[minValue, maxValue]`.
 *
 * The summary fields (mean, median, outlier counts) are computed over the
 * full sampled population — including voxels outside `[minValue, maxValue]`
 * — so the caller can surface "↑ N voxels above the displayed scale
 * (data max: ...)" warnings clinicians need to see metal artifacts,
 * contrast spikes, etc. that the percentile clip otherwise hides.
 */
export interface IntensityHistogram {
    bins: number[];
    minValue: number;
    maxValue: number;
    /** Smallest voxel intensity of the volume the histogram was sampled from. */
    dataMin: number;
    /** Largest voxel intensity of the volume the histogram was sampled from. */
    dataMax: number;
    /** Mean of all sampled voxel intensities (full data range). */
    sampleMean: number;
    /** Approximate median of sampled voxel intensities (wide-bin midpoint). */
    sampleMedian: number;
    /** Sampled voxels with intensity below `minValue`. */
    voxelsBelowDisplay: number;
    /** Sampled voxels with intensity above `maxValue`. */
    voxelsAboveDisplay: number;
    /** Total number of sampled voxels (denominator for outlier ratios). */
    totalSampled: number;
}

/**
 * Internal info for interpolation during pixel data population
 */
interface InterpolationInfo {
    /** Map of z-index to existing DICOM slice */
    sliceMap: Map<number, DicomImageSlice>;
    /** Total number of slices including interpolated ones */
    totalSlices: number;
    /** Interpolation method to use */
    interpolationMethod: InterpolationMethod;
}

/**
 * Validation status for slice position analysis
 */
export enum SliceValidationStatus {
    /** All slices present with uniform spacing - can reconstruct directly */
    CompleteReconstruction = 'CompleteReconstruction',
    /** Uniform spacing but some slices are missing - could interpolate */
    MissingSlices = 'MissingSlices',
    /** Non-uniform spacing that could be corrected via resampling */
    ResamplingNeeded = 'ResamplingNeeded',
    /** Cannot construct 3D image (no slices, not 3D, or other fundamental issues) */
    CannotConstruct = 'CannotConstruct',
}

/**
 * Information about a gap between consecutive slices.
 *
 * PHI-free by construction, and must stay that way: this type is embedded in
 * validation `reason` strings and in the messages `Image3D.fromSeries` throws,
 * and those messages are console-logged by `dicomProcessor.addNewImage3D`. A
 * gap is therefore identified by its position in the stack — an index and two
 * z-projections locate it exactly as well for debugging, and identify no
 * patient. Never add a slice identifier here.
 */
export interface SliceGapInfo {
    /** The gap distance in mm */
    gap: number;
    /**
     * Index of this gap in the z-sorted slice stack: gap `i` lies between
     * sorted slices `i` and `i + 1`.
     */
    gapIndex: number;
    /** z-projection of the lower slice of the pair, in mm along the stack normal */
    lowerZMm: number;
    /** z-projection of the upper slice of the pair, in mm along the stack normal */
    upperZMm: number;
}

/**
 * Renders gaps for a diagnostic message. Counts, indices and millimetres only
 * — never a UID. See {@link SliceGapInfo}.
 */
export function describeSliceGaps(gaps: readonly SliceGapInfo[]): string {
    if (gaps.length === 0) {
        return 'none';
    }
    return gaps
        .map(
            (g) =>
                `#${g.gapIndex} (z ${g.lowerZMm.toFixed(2)} mm to ` +
                `${g.upperZMm.toFixed(2)} mm, gap ${g.gap.toFixed(2)} mm)`
        )
        .join(', ');
}

/**
 * Result of slice position validation
 */
export interface SliceValidationResult {
    /** Validation status */
    status: SliceValidationStatus;
    /** Reason for the validation status (empty if CompleteReconstruction) */
    reason: string;
    /** Expected z-resolution based on slice positions (undefined if cannot compute) */
    expectedZResolution: number | undefined;
    /** Slice z-projections sorted ascending: [z-projection along stack normal, sopInstanceUid] */
    sortedSliceZProjections: Array<[number, string]>;
    /** Image Position Patient (DICOM) of the bottom-most slice */
    bottomSlicePatientPosition: ReadonlyVec3;
    /** Z-direction normal vector */
    zDirection: ReadonlyVec3;
    /** Gaps that deviate from expected resolution */
    invalidGaps: SliceGapInfo[];
    /** Detected missing slice count (if MissingSlices status) */
    missingSliceCount: number;
}

/**
 * Finds the base (most likely original) slice spacing using statistical analysis.
 * Uses histogram/clustering to find the smallest gap that appears most frequently.
 *
 * The algorithm handles both:
 * - Missing slices (gaps that are integer multiples of base spacing)
 * - MR variations (slight deviations in slice spacing due to tilted acquisition)
 *
 * @param gaps Array of gap distances between consecutive slices
 * @param tolerance Tolerance for grouping similar gaps (absolute, in mm)
 * @returns The detected base spacing
 */
function findBaseSpacing(gaps: number[], tolerance: number): number {
    if (gaps.length === 0) {
        return 1.0;
    }

    if (gaps.length === 1) {
        return gaps[0];
    }

    // Sort gaps to help with clustering
    const sortedGaps = [...gaps].sort((a, b) => a - b);

    // Use adaptive tolerance for bucketing: at least the specified tolerance,
    // but also consider relative tolerance based on median gap (handles MR variations)
    const medianGap = sortedGaps[Math.floor(sortedGaps.length / 2)];
    const relativeTolerance = medianGap * 0.08; // 8% relative tolerance for clustering
    const bucketTolerance = Math.max(tolerance, relativeTolerance);

    // Group gaps into buckets using the adaptive tolerance
    const buckets: Array<{ representative: number; gaps: number[] }> = [];

    for (const gap of gaps) {
        // Find existing bucket within tolerance, using a fixed representative
        let foundBucket = false;
        for (const bucket of buckets) {
            if (Math.abs(gap - bucket.representative) <= bucketTolerance) {
                bucket.gaps.push(gap);
                foundBucket = true;
                break;
            }
        }
        if (!foundBucket) {
            // Use the first value added as the fixed representative during bucketing
            buckets.push({ representative: gap, gaps: [gap] });
        }
    }

    // After clustering, compute a stable representative (median) for each bucket
    for (const bucket of buckets) {
        const sortedBucketGaps = [...bucket.gaps].sort((a, b) => a - b);
        const medianIndex = Math.floor(sortedBucketGaps.length / 2);
        bucket.representative = sortedBucketGaps[medianIndex];
    }
    // Find the best base spacing using a scoring system
    // Score = count / representative (favors smaller gaps with high frequency)
    let bestSpacing = gaps[0];
    let bestScore = 0;

    for (const bucket of buckets) {
        const score = bucket.gaps.length / bucket.representative;
        if (score > bestScore) {
            bestScore = score;
            bestSpacing = bucket.representative;
        }
    }

    return bestSpacing;
}

/**
 * Validates slice positions for 3D image reconstruction.
 * Analyzes the spatial arrangement of DICOM slices to determine if they can form a valid 3D volume.
 *
 * Uses statistical gap analysis to correctly detect the base slice spacing even when
 * multiple slices are missing. This avoids the pitfall of calculating expected resolution
 * as totalDistance / (count - 1), which gives incorrect results when slices are missing.
 *
 * Takes the structural {@link SliceGeometryInventory} rather than a parsed
 * `Series` so the SAME algorithm — and therefore the same grid — serves both
 * the local-file path (a parsed `Series` satisfies the interface verbatim) and
 * the progressive database path (see
 * {@link ImageSeriesManifest.toSliceGeometryInventory}). `findBaseSpacing` is
 * statistical over the observed gaps, so the inventory must always be the full
 * slice list, never the subset that has arrived so far.
 *
 * @param series The full slice-geometry inventory of the series
 * @param tolerance Tolerance for slice spacing deviation in mm (default: 0.2mm)
 * @returns Validation result with status and detailed information
 */
export function validateSlicePositions(
    series: SliceGeometryInventory,
    tolerance: number = Image3D.SliceResolutionTolerance
): SliceValidationResult {
    const emptyResult: SliceValidationResult = {
        status: SliceValidationStatus.CannotConstruct,
        reason: '',
        expectedZResolution: undefined,
        sortedSliceZProjections: [],
        bottomSlicePatientPosition: vec3.create(),
        zDirection: vec3.create(),
        invalidGaps: [],
        missingSliceCount: 0,
    };

    const imageSlices = series.dicomImageSlices;

    // Check basic requirements
    if (imageSlices.length === 0) {
        return {
            ...emptyResult,
            reason: 'No image slices in series',
        };
    }

    if (!series.is3DImage) {
        return {
            ...emptyResult,
            reason: 'Series is not marked as a 3D image',
        };
    }

    // Single slice is always valid for reconstruction
    if (imageSlices.length === 1) {
        const slice = imageSlices[0];
        const zdir = vec3.fromValues(
            slice.imageOrientation[6],
            slice.imageOrientation[7],
            slice.imageOrientation[8]
        );
        return {
            status: SliceValidationStatus.CompleteReconstruction,
            reason: '',
            expectedZResolution:
                slice.sliceThickness > 0 ? slice.sliceThickness : 1.0,
            sortedSliceZProjections: [
                [vec3.dot(zdir, slice.imagePosition), slice.sopInstanceUid],
            ],
            bottomSlicePatientPosition: slice.imagePosition,
            zDirection: zdir,
            invalidGaps: [],
            missingSliceCount: 0,
        };
    }

    const exampleSlice = imageSlices[0];

    // Get z-direction from image orientation
    const zdir = vec3.fromValues(
        exampleSlice.imageOrientation[6],
        exampleSlice.imageOrientation[7],
        exampleSlice.imageOrientation[8]
    );

    // Calculate slice positions projected onto z-axis
    const slicePositions = new Array<[number, string]>();
    let bottomSlicePos: ReadonlyVec3 = vec3.create();
    let minz = Number.MAX_SAFE_INTEGER;

    for (const imageSlice of imageSlices) {
        const imagePos = imageSlice.imagePosition;
        const zcoord = vec3.dot(zdir, imagePos);

        slicePositions.push([zcoord, imageSlice.sopInstanceUid]);
        if (zcoord < minz) {
            bottomSlicePos = imagePos;
            minz = zcoord;
        }
    }

    slicePositions.sort((a, b) => a[0] - b[0]);

    // Calculate gaps between consecutive slices
    // `pos` is slicePositions[i + 1]; slicePositions[i] is the lower slice.
    // Positional identification only — see SliceGapInfo: naming the slices by
    // their SOP Instance UIDs here leaked PHI into every log that printed the
    // resulting message.
    const gaps: SliceGapInfo[] = slicePositions.slice(1).map((pos, i) => ({
        gap: Math.abs(pos[0] - slicePositions[i][0]),
        gapIndex: i,
        lowerZMm: slicePositions[i][0],
        upperZMm: pos[0],
    }));

    const gapValues = gaps.map((g) => g.gap);

    // Find the base spacing using statistical analysis
    // This correctly identifies the original slice spacing even when multiple slices are missing
    const baseSpacing = findBaseSpacing(gapValues, tolerance);

    // Use relative tolerance for MR compatibility (slight variations in slice spacing)
    // At least the absolute tolerance, but also 8% of base spacing for MR robustness
    const effectiveTolerance = Math.max(tolerance, baseSpacing * 0.08);

    // Validate all gaps against base spacing
    // A gap is valid if it's approximately N * baseSpacing where N >= 1
    let totalMissingSlices = 0;
    const invalidGaps: SliceGapInfo[] = [];

    for (const gapInfo of gaps) {
        const ratio = gapInfo.gap / baseSpacing;
        const nearestInt = Math.round(ratio);
        const expectedGap = nearestInt * baseSpacing;
        const deviation = Math.abs(gapInfo.gap - expectedGap);

        if (deviation > effectiveTolerance) {
            // Gap is not a multiple of base spacing - truly non-uniform
            invalidGaps.push(gapInfo);
        } else if (nearestInt > 1) {
            // Gap is N * baseSpacing where N > 1 - missing slices
            totalMissingSlices += nearestInt - 1;
        }
        // else: nearestInt === 1 and deviation <= tolerance - normal gap
    }

    // Classification based on analysis results
    if (invalidGaps.length === 0 && totalMissingSlices === 0) {
        // All gaps are uniform within tolerance - complete reconstruction
        return {
            status: SliceValidationStatus.CompleteReconstruction,
            reason: '',
            expectedZResolution: baseSpacing,
            sortedSliceZProjections: slicePositions,
            bottomSlicePatientPosition: bottomSlicePos,
            zDirection: zdir,
            invalidGaps: [],
            missingSliceCount: 0,
        };
    }

    if (invalidGaps.length === 0 && totalMissingSlices > 0) {
        // All gaps are multiples of base spacing - missing slices detected
        // Collect the gaps that indicate missing slices for reporting
        const missingSliceGaps = gaps.filter((gapInfo) => {
            const ratio = gapInfo.gap / baseSpacing;
            const nearestInt = Math.round(ratio);
            return nearestInt > 1;
        });

        return {
            status: SliceValidationStatus.MissingSlices,
            reason: `${totalMissingSlices} slice(s) appear to be missing`,
            expectedZResolution: baseSpacing,
            sortedSliceZProjections: slicePositions,
            bottomSlicePatientPosition: bottomSlicePos,
            zDirection: zdir,
            invalidGaps: missingSliceGaps,
            missingSliceCount: totalMissingSlices,
        };
    }

    // Gaps are non-uniform - resampling would be needed
    return {
        status: SliceValidationStatus.ResamplingNeeded,
        reason:
            'Non-uniform slice spacing detected. Offending gaps: ' +
            describeSliceGaps(invalidGaps),
        expectedZResolution: baseSpacing,
        sortedSliceZProjections: slicePositions,
        bottomSlicePatientPosition: bottomSlicePos,
        zDirection: zdir,
        invalidGaps,
        missingSliceCount: 0,
    };
}

/**
 * Seeded intensity range used while a progressively loaded image is still
 * incomplete, keyed by modality.
 *
 * These are **display-scale placeholders, not measured data ranges**. They
 * exist so that `minValue`/`maxValue` are stable numbers for the whole load:
 * `minValue` is a live shader uniform and the drawing-constraint range, so a
 * value that shifts as slices arrive re-scales the slice the clinician is
 * currently reading, mid-read. A placeholder that never moves is the
 * conservative choice; the measured range becomes available once
 * {@link Image3D.isComplete} is true, and consumers that need a measured range
 * must gate on {@link Image3D.hasMeasuredIntensityRange}.
 *
 * - CT: the standard 12-bit CT reconstruction range in HU.
 * - PT: unsigned 16-bit stored-value range; PET activity concentration after
 *   rescale is of this order.
 * - MR / other: unsigned 12-bit magnitude range.
 */
function seededIntensityRange(modality: Modality): readonly [number, number] {
    switch (modality) {
        case Modality.CT:
            return [-1024, 3071];
        case Modality.PT:
            return [0, 65535];
        default:
            return [0, 4095];
    }
}

/**
 * The window an image is shown with when it names none of its own and none
 * can be measured from it: the conventional soft-tissue window.
 */
function conventionalWindowLevel(): WindowLevel {
    return new WindowLevel(400, 20);
}

/**
 * Window/level held fixed for the whole progressive load. Prefers the value
 * the source actually declared (manifest Window Center / Window Width, then
 * the anchor slice's own); only when the series declares none does it fall
 * back to a modality default spanning {@link seededIntensityRange}.
 *
 * Nothing here is recomputed at completion: an unrequested contrast change is
 * exactly the hazard progressive loading must not introduce.
 */
function seededWindowLevel(
    acquisition: WindowLevel | undefined,
    modality: Modality,
    range: readonly [number, number]
): WindowLevel {
    if (acquisition !== undefined) {
        return acquisition;
    }
    if (modality === Modality.CT) {
        return conventionalWindowLevel();
    }
    return new WindowLevel(
        range[1] - range[0],
        Math.round((range[0] + range[1]) / 2)
    );
}

/**
 * Arrival bookkeeping for an {@link Image3D} built from a manifest.
 *
 * "Pending" is a THIRD state, deliberately kept apart from interpolation: a
 * slice that has not arrived yet is not synthetic anatomy, and a slice that
 * never arrives must stay explicitly missing rather than being quietly
 * interpolated into something that looks complete.
 */
interface ProgressiveLoadState {
    /** The full inventory the volume was built from. */
    readonly manifest: ImageSeriesManifest;
    /** z-index of every manifest slice. Full inventory, never a subset. */
    readonly zIndexBySopUid: ReadonlyMap<string, number>;
    /**
     * The inverse of {@link zIndexBySopUid}, indexed by z. The manifest maps
     * to distinct z-indices, so the inverse is well defined; a z the manifest
     * does not cover holds `undefined`.
     */
    readonly sopUidByZ: readonly (string | undefined)[];
    /** SOP UIDs whose pixels have been written into the buffers. */
    readonly arrived: Set<string>;
    /** SOP UIDs whose retrieval failed terminally. Never interpolated. */
    readonly unavailable: Set<string>;
    /**
     * Lowest and highest voxel value across the slices inserted so far,
     * accumulated as each one is written. Once every slice has arrived these
     * span exactly the same voxels a scan of the whole buffer would, so the
     * measured range is already known when the load finishes.
     *
     * A slice carrying only NaN leaves them untouched, matching a scan, which
     * compares rather than calls Math.min/Math.max and so steps over NaN.
     */
    runningMinValue: number;
    runningMaxValue: number;
    /** Frozen display seeds — see {@link seededIntensityRange}. */
    readonly seededMinValue: number;
    readonly seededMaxValue: number;
    readonly seededWindowLevel: WindowLevel;
    /** Guards the one-shot cache reset at completion. */
    completionCachesReset: boolean;
}

/** Construction input for the progressive (manifest-driven) path. */
interface ProgressiveInit {
    readonly manifest: ImageSeriesManifest;
    readonly zIndexBySopUid: ReadonlyMap<string, number>;
}

/**
 * {@link Image3D} is a container for 3D image information parsed from DICOM {@link DicomImageSlice} objects.
 */
export class Image3D implements TrackableObject {
    readonly trackableKind = 'image' as const;

    /**
     * Unique runtime identifier generated when the {@link Image3D} is
     * constructed. Accepts an existing id so a persisted image can be
     * round-tripped without minting a new identity.
     */
    public readonly id: ImageId;

    private tracker: ChangeTracker | null = null;

    /**
     * Every value derived from the whole volume. They register themselves on
     * construction, so {@link resetDerivedValues} covers all of them and
     * cannot fall out of step with the fields.
     *
     * Declared before them: field initialisers run in declaration order, and a
     * derived value declared above this would have nothing to register on.
     */
    private readonly derivations: ResettableDerivation[] = [];

    public static readonly SliceResolutionTolerance = 0.2; // mm
    /**
     * Maximum number of consecutive missing slices that will be automatically
     * interpolated when constructing a 3D volume.
     *
     * This limit is intentionally conservative to bound the through-plane distance over
     * which synthetic data are generated (for typical CT protocols this
     * corresponds to only a few millimetres). Beyond this range, internal
     * image‑quality evaluations showed a risk of degraded anatomical fidelity
     * and potentially misleading structures in interpolated regions.
     *
     * Gaps larger than this threshold are therefore treated as clinically
     * significant and must be handled explicitly by the workflow (e.g. by
     * rejecting the series or requiring user review), rather than being
     * silently auto‑filled by the software.
     *
     * The detailed justification, hazard analysis, and validation evidence for
     * this limit are maintained in the IEC 62304 design and risk management
     * documentation for slice interpolation.
     */
    public static readonly MaxAutoInterpolatedSlices = 5;

    /**
     * Create a {@link Image3D} from DICOM object, including the image slice data.
     * Only succeeds for series with complete, uniformly-spaced slices unless
     * interpolation is enabled via options.
     *
     * Use {@link validateSlicePositions} to check validity before calling this method.
     *
     * @param series {@link Series}
     * @param options Optional configuration for interpolation
     * @returns {@link Image3D} or null if series cannot form a 3D image
     * @throws Error if slice spacing is non-uniform or slices are missing (without interpolation enabled)
     */
    static fromSeries(
        series: Series,
        options?: Image3DOptions
    ): Image3D | null {
        const validation = validateSlicePositions(series);

        if (validation.status === SliceValidationStatus.CannotConstruct) {
            return null;
        }

        if (validation.status === SliceValidationStatus.MissingSlices) {
            if (options?.interpolateMissingSlices) {
                return Image3D.fromSeriesWithInterpolation(
                    series,
                    validation,
                    options
                );
            }
            throw new Error(
                `Missing slices detected: ${validation.reason}. ` +
                    `Offending gaps: ${describeSliceGaps(validation.invalidGaps)}`
            );
        }

        if (validation.status === SliceValidationStatus.ResamplingNeeded) {
            throw new Error(
                `Non-uniform slice spacing detected, this is not supported. ${validation.reason}`
            );
        }

        const imageSlices = series.dicomImageSlices;
        const exampleSlice = imageSlices[0];

        const resolution = vec3.fromValues(
            exampleSlice.pixelSpacing[0],
            exampleSlice.pixelSpacing[1],
            validation.expectedZResolution ?? 1.0
        );

        const size = vec3.fromValues(
            exampleSlice.cols,
            exampleSlice.rows,
            imageSlices.length
        );

        return new Image3D(
            Grid.fromDicom(
                size,
                resolution,
                validation.bottomSlicePatientPosition,
                exampleSlice.imageOrientation
            ),
            series.seriesInstanceUID,
            series.frameOfReferenceUid,
            exampleSlice,
            series
        );
    }

    /**
     * Create an Image3D with interpolation for missing slices.
     * This creates a volume with the full expected number of slices,
     * interpolating pixel data for any missing slices.
     */
    private static fromSeriesWithInterpolation(
        series: Series,
        validation: SliceValidationResult,
        options: Image3DOptions
    ): Image3D {
        if (
            validation.expectedZResolution === undefined ||
            validation.expectedZResolution <= 1e-4
        ) {
            throw new Error(
                `Cannot perform interpolation without valid z-resolution (${validation.expectedZResolution})`
            );
        }
        const imageSlices = series.dicomImageSlices;
        const exampleSlice = imageSlices[0];
        const zResolution = validation.expectedZResolution;

        // Calculate total slices including interpolated ones
        const totalSlices = imageSlices.length + validation.missingSliceCount;

        // Build a map of z-index -> slice for existing slices
        const minZ = validation.sortedSliceZProjections[0][0];
        const sliceMap = new Map<number, DicomImageSlice>();

        for (const imageSlice of imageSlices) {
            const zCoord = vec3.dot(
                validation.zDirection,
                imageSlice.imagePosition
            );
            const zIndex = Math.round((zCoord - minZ) / zResolution);
            sliceMap.set(zIndex, imageSlice);
        }

        // Check safety limits
        const existingIndices = Array.from(sliceMap.keys()).sort(
            (a, b) => a - b
        );
        const maxConsecutive = getMaxConsecutiveGap(
            existingIndices,
            totalSlices
        );
        const maxAllowed =
            options.maxInterpolatedSlices ?? Image3D.MaxAutoInterpolatedSlices;

        if (maxConsecutive > maxAllowed) {
            throw new Error(
                `Too many consecutive missing slices: ${maxConsecutive} ` +
                    `(max allowed: ${maxAllowed}). Consider increasing maxInterpolatedSlices if intentional.`
            );
        }

        const resolution = vec3.fromValues(
            exampleSlice.pixelSpacing[0],
            exampleSlice.pixelSpacing[1],
            zResolution
        );

        const size = vec3.fromValues(
            exampleSlice.cols,
            exampleSlice.rows,
            totalSlices
        );

        const image = new Image3D(
            Grid.fromDicom(
                size,
                resolution,
                validation.bottomSlicePatientPosition,
                exampleSlice.imageOrientation
            ),
            series.seriesInstanceUID,
            series.frameOfReferenceUid,
            exampleSlice,
            series,
            {
                sliceMap,
                totalSlices,
                interpolationMethod:
                    options.interpolationMethod ?? InterpolationMethod.Linear,
            }
        );

        return image;
    }

    /**
     * Create a progressively loadable {@link Image3D} from a series manifest
     * plus a single parsed anchor slice.
     *
     * The geometry comes entirely from the manifest and is validated through
     * the same {@link validateSlicePositions} / {@link Grid.fromDicom} pair the
     * local-file path uses — there is deliberately no second grid builder. The
     * pixel buffers are allocated full-size up front and every manifest slice
     * other than the anchor starts *pending*; pixels arrive later through
     * {@link insertSlice}.
     *
     * The anchor slice is mandatory, not optional: modality, patient position,
     * Study Instance UID, SOP Class UID and burned-in-annotation status are all
     * read off a parsed slice and no amount of manifest metadata substitutes.
     *
     * Fails fast rather than degrading:
     * - a manifest whose inventory is short of its declared expected count
     *   (the source is still receiving the series) — a grid sized to a partial
     *   inventory is wrong for every slice that arrives later;
     * - a geometry that is not a complete, uniformly spaced stack. Streaming
     *   never interpolates: missing slices stay missing and visible as such.
     *
     * Error messages carry counts and indices only, never SOP Instance UIDs.
     *
     * @param manifest Full slice inventory and geometry of the series
     * @param anchorSlice One parsed slice of this series, already retrieved
     * @param series The (initially near-empty) series the slices belong to
     * @param id Optional existing image id, for round-tripping a persisted image
     * @throws DomainValidationError if the manifest cannot form a 3D volume
     */
    static fromManifest(
        manifest: ImageSeriesManifest,
        anchorSlice: DicomImageSlice,
        series: Series,
        id: ImageId = newImageId()
    ): Image3D {
        if (!manifest.isInventoryComplete) {
            throw new DomainValidationError(
                `Image series manifest lists ${manifest.sliceCount} of ` +
                    `${manifest.expectedSliceCount ?? manifest.sliceCount} expected slices; ` +
                    'refusing to build a volume from a partial inventory'
            );
        }
        // Membership, not position: any manifest slice may be the anchor.
        const anchorIsListed = manifest.slices.some(
            (slice) => slice.sopInstanceUid === anchorSlice.sopInstanceUid
        );
        if (!anchorIsListed) {
            throw new DomainValidationError(
                'Anchor slice is not part of the image series manifest'
            );
        }

        const validation = validateSlicePositions(
            manifest.toSliceGeometryInventory()
        );
        if (
            validation.status !== SliceValidationStatus.CompleteReconstruction
        ) {
            throw new DomainValidationError(
                'Image series manifest does not describe a complete, uniformly ' +
                    `spaced slice stack (${validation.status}: ` +
                    `${validation.missingSliceCount} missing, ` +
                    `${validation.invalidGaps.length} non-uniform gap(s))`
            );
        }

        const resolution = vec3.fromValues(
            manifest.pixelSpacingMm[0],
            manifest.pixelSpacingMm[1],
            validation.expectedZResolution ?? manifest.sliceThicknessMm
        );
        const size = vec3.fromValues(
            manifest.columns,
            manifest.rows,
            manifest.sliceCount
        );
        const grid = Grid.fromDicom(
            size,
            resolution,
            validation.bottomSlicePatientPosition,
            manifest.imageOrientationPatient
        );

        // Derive every manifest slice's z-index from the SAME grid the pixels
        // will be written through, so manifest bookkeeping and pixel placement
        // cannot disagree.
        const zIndexBySopUid = new Map<string, number>();
        for (const slice of manifest.slices) {
            const zIndex = Math.floor(
                grid.toVoxelSpace(slice.imagePositionPatientMm)[2]
            );
            if (zIndex < 0 || zIndex >= grid.sizeZ) {
                throw new DomainValidationError(
                    `Image series manifest slice maps to z-index ${zIndex}, ` +
                        `outside the volume of ${grid.sizeZ} slices`
                );
            }
            zIndexBySopUid.set(slice.sopInstanceUid, zIndex);
        }
        if (new Set(zIndexBySopUid.values()).size !== manifest.sliceCount) {
            throw new DomainValidationError(
                'Image series manifest slices do not map to distinct z-indices'
            );
        }

        const image = new Image3D(
            grid,
            manifest.seriesInstanceUid,
            manifest.frameOfReferenceUid,
            anchorSlice,
            series,
            undefined,
            id,
            { manifest, zIndexBySopUid }
        );
        image.insertSlice(anchorSlice);
        return image;
    }

    /**
     * Update the pixel data from the DICOM slices in the series
     */
    private updatePixelDataFromSeries(): void {
        for (const slice of this.series.dicomImageSlices) {
            // determine the z-coordinate of the slice in voxel space
            const voxelCoords = this.grid.toVoxelSpace(slice.imagePosition);
            const sliceSize = this.grid.sizeX * this.grid.sizeY;
            // use Math.floor to get the slice index (DICOM image position is at the center of the slice)
            const zcoord = Math.floor(voxelCoords[2]);
            if (zcoord < 0 || zcoord >= this.grid.sizeZ) {
                // No SOP UID: the message reaches logs via ingest warnings.
                throw new Error(
                    `Slice position outside of image volume (pos ${slice.imagePosition}, z ${zcoord})`
                );
            }
            const pixelIdx = zcoord * sliceSize;
            this.pixelData.set(slice.pixelData, pixelIdx);
        }
    }

    /**
     * Assemble Float32 volume directly from DICOM slice Float32 data.
     * Used for PET images to bypass Int16 intermediate entirely.
     */
    private updateFloatPixelDataFromSeries(): void {
        const totalVoxels = this.grid.sizeX * this.grid.sizeY * this.grid.sizeZ;
        this._floatPixelData = new Float32Array(totalVoxels);
        const sliceSize = this.grid.sizeX * this.grid.sizeY;

        for (const slice of this.series.dicomImageSlices) {
            const voxelCoords = this.grid.toVoxelSpace(slice.imagePosition);
            const zcoord = Math.floor(voxelCoords[2]);
            if (zcoord < 0 || zcoord >= this.grid.sizeZ) {
                throw new Error(
                    `Slice position outside of image volume ` +
                        `(pos ${slice.imagePosition}, z ${zcoord})`
                );
            }
            this._floatPixelData.set(slice.floatPixelData, zcoord * sliceSize);
        }
    }

    public hasSlice(sopInstanceUid: string): boolean {
        for (const slice of this.series.dicomImageSlices) {
            if (slice.sopInstanceUid === sopInstanceUid) {
                return true;
            }
        }
        return false;
    }

    /**
     * Find the SOP Instance UID of the slice at the given
     * normal position (projection of the slice center onto
     * the grid's Z unit vector, in mm). This is NOT necessarily
     * the same as the patient-space Z coordinate for non-HFS
     * orientations.
     */
    public getUidForSlice(normalPosition: number): string | undefined {
        const normalDir = this.grid.getUnitZ();
        const slice = this.series.dicomImageSlices.find(
            (slice) =>
                Math.abs(
                    vec3.dot(normalDir, slice.imagePosition) - normalPosition
                ) < 0.1
        );
        return slice?.sopInstanceUid ?? undefined;
    }

    public getAcquisitionWindowLevel(): WindowLevel | undefined {
        return this._acquisitionWindowLevel;
    }

    private readonly measuredAutoWindowLevel = new DerivedValue<
        WindowLevel | undefined
    >(
        () =>
            this.firstSlice.modality === Modality.CT
                ? computeAutoWindowLevelForCT(
                      this.floatPixelData,
                      this.grid.sizeX,
                      this.grid.sizeY,
                      this.grid.sizeZ
                  )
                : computeAutoWindowLevelFromHistogram(
                      this.floatPixelData,
                      this.grid.sizeX,
                      this.grid.sizeY,
                      this.grid.sizeZ
                  ),
        this.derivations
    );

    /**
     * Auto window/level over the whole volume, or `undefined` when it cannot
     * be computed.
     *
     * Returns `undefined` while a progressive load is incomplete: an auto W/L
     * derived from a partial volume is not the series' auto W/L, and both
     * callers (the windowing preset list and the windowing controls) already
     * treat `undefined` as "no Auto preset offered". Offering nothing is safe;
     * offering a number that would change under the user is not.
     */
    public getAutoWindowLevel(): WindowLevel | undefined {
        if (this.isDisplayStateFrozen) {
            return undefined;
        }
        return this.measuredAutoWindowLevel.get();
    }

    private readonly measuredMrAutoWindowLevelPresets = new DerivedValue<
        WindowingPreset[]
    >(() => {
        if (this.firstSlice.modality !== Modality.MR) {
            return [];
        }
        return MR_AUTO_WL_ALGORITHMS.map((algo) => {
            const wl = algo.compute(
                this.floatPixelData,
                this.grid.sizeX,
                this.grid.sizeY,
                this.grid.sizeZ
            );
            return {
                name: algo.name,
                ww: wl.window,
                wc: wl.level,
            };
        });
    }, this.derivations);

    /**
     * Returns all MR-specific auto WL algorithm results as presets.
     * Only meaningful for MR modality; returns empty array for CT/PT.
     *
     * Also empty while a progressive load is incomplete — the presets are
     * whole-volume statistics, and a preset list that silently changes its
     * numbers mid-load is worse than a preset list that is not offered yet.
     * Nothing is held in that case, so the real presets are derived on the
     * first read after completion.
     */
    public getMrAutoWindowLevelPresets(): WindowingPreset[] {
        if (this.isDisplayStateFrozen) {
            return [];
        }
        return this.measuredMrAutoWindowLevelPresets.get();
    }

    /**
     * The window/level to display this image with when the user has not set
     * one.
     *
     * For a progressively loaded image the seeded value comes BEFORE the auto
     * W/L in the chain, so the answer is fixed for the whole life of the
     * image: it must not silently become the full-volume auto W/L the moment
     * the last slice lands. Nothing changes for a parsed series, whose auto
     * W/L is available from the start.
     */
    public getDefaultWindowLevel(): WindowLevel {
        return (
            this.windowLevel ?? // first try user-set WL to keep settings persistent across the workflows
            this.getAcquisitionWindowLevel() ??
            this._progressive?.seededWindowLevel ??
            this.getAutoWindowLevel() ??
            conventionalWindowLevel()
        );
    }

    /**
     * The window Reset returns this image to: the window it was acquired
     * with, else its automatic window, else the window a progressive load
     * first showed it with, else the conventional window. The window the
     * user set plays no part in the answer.
     */
    public getResetWindowLevel(): WindowLevel {
        return (
            this.getAcquisitionWindowLevel() ??
            this.getAutoWindowLevel() ??
            this._progressive?.seededWindowLevel ??
            conventionalWindowLevel()
        );
    }

    /**
     * The window this volume is drawn with when it first reaches a display:
     * the window the user has set, else for a PET volume its full value
     * range (activity has no conventional window, and every PET readout and
     * control spans that range), else {@link getDefaultWindowLevel}. The
     * acquired window of a PET stays available as a named window.
     *
     * While a PET loads, its full value range is that of the seeded
     * placeholder, and a window it holds carries the placeholder's level
     * bounds. Once the load has completed, a held window that still carries
     * them is not the answer: {@link heldWindowOffPlaceholderBounds} is.
     * Every other held window is the answer, before and after completion.
     */
    public windowLevelForFirstDisplay(): WindowLevel {
        return (
            this.heldWindowOffPlaceholderBounds ??
            this.windowLevel ??
            (this.firstSlice.modality === Modality.PT
                ? fullRangeWindowLevel(this.maxValue)
                : undefined) ??
            this.getDefaultWindowLevel()
        );
    }

    /**
     * The window that takes the place of the held one when the held one
     * still carries the level bounds of the seeded placeholder although the
     * load has completed, else undefined. Only a PET's first window is
     * taken from the intensity range, so this is undefined for every other
     * modality, for a volume that was whole from the start, for a load that
     * has not completed or ended short, and for a volume with no positive
     * value.
     *
     * In the volume's stored value units. The placeholder level bounds are
     * those of the placeholder full range (0 to the seeded maximum), the
     * measured ones those of the measured full range (0 to the measured
     * maximum, rounded up). Where the two are the same, a held window
     * cannot be told from one set on the measured range, and nothing takes
     * its place.
     *
     * Otherwise a held window placed on the placeholder level bounds (as
     * {@link windowLevelOnBounds} places it: the bounds themselves, or one
     * of them widened to admit the center) gives way to
     * - the measured full range, when no window the user chose is on
     *   record (see {@link setWindowLevel}) or the held window is the
     *   placeholder full range itself;
     * - else its own width and center on the measured level bounds, as
     *   {@link windowLevelOnBounds} places them: a window the user chose is
     *   kept whatever its width, and only its level bounds change.
     *
     * Neither carries the placeholder level bounds, so once the volume
     * holds one of them, a window set on it later is never replaced.
     */
    private get heldWindowOffPlaceholderBounds(): WindowLevel | undefined {
        const state = this._progressive;
        const held = this._windowLevel;
        if (
            state === undefined ||
            !this.isComplete ||
            this.firstSlice.modality !== Modality.PT ||
            held === undefined
        ) {
            return undefined;
        }
        const seeded = fullRangeWindowLevel(state.seededMaxValue);
        const measured = fullRangeWindowLevel(this.maxValue);
        if (
            seeded === undefined ||
            measured === undefined ||
            (measured.minLevel === seeded.minLevel &&
                measured.maxLevel === seeded.maxLevel) ||
            !windowLevelOnBounds(held.window, held.level, seeded).equals(held)
        ) {
            return undefined;
        }
        if (!this._isWindowLevelSetByUser || held.equals(seeded)) {
            return measured;
        }
        return windowLevelOnBounds(held.window, held.level, measured);
    }

    private readonly sampledHistogram = new DerivedValueByKey<
        number,
        IntensityHistogram | null
    >((numBins) => this.computeIntensityHistogram(numBins), this.derivations);

    /**
     * Returns a sampled intensity histogram cropped to the 2nd–99.5th percentile
     * range, suitable for visualizing the distribution of clinically relevant
     * voxel values (excluding outliers like air or metal artifacts).
     *
     * Uses the same 4×4×2 stride sampling pattern as
     * `computeAutoWindowLevelFromHistogram`. Cached on the instance.
     *
     * Returns `null` while a progressive load is incomplete: the sampler
     * would count the zero-filled rows of slices that have not arrived, and
     * the resulting distribution would visibly redraw itself as they land.
     * The sole caller (the windowing hint) already renders nothing for `null`.
     */
    public getIntensityHistogram(
        numBins: number = 64
    ): IntensityHistogram | null {
        if (this.isDisplayStateFrozen) {
            return null;
        }
        return this.sampledHistogram.get(numBins);
    }

    /** {@link getIntensityHistogram} over the whole volume, unconditionally. */
    private computeIntensityHistogram(
        numBins: number
    ): IntensityHistogram | null {
        const dataMin = this.minValue;
        const dataMax = this.maxValue;
        const dataSpan = dataMax - dataMin;
        if (dataSpan <= 0) {
            return null;
        }

        const data = this.floatPixelData;
        const sx = this.grid.sizeX;
        const sy = this.grid.sizeY;
        const sz = this.grid.sizeZ;
        const stepX = 4;
        const stepY = 4;
        const stepZ = 2;
        const sliceSize = sx * sy;

        const WIDE_BINS = 1024;
        const wide = new Uint32Array(WIDE_BINS);
        let total = 0;
        let sum = 0;

        for (let z = 0; z < sz; z += stepZ) {
            const zOff = z * sliceSize;
            for (let y = 0; y < sy; y += stepY) {
                const yOff = y * sx;
                for (let x = 0; x < sx; x += stepX) {
                    const v = data[zOff + yOff + x];
                    const bin = Math.min(
                        WIDE_BINS - 1,
                        Math.max(
                            0,
                            Math.floor(((v - dataMin) / dataSpan) * WIDE_BINS)
                        )
                    );
                    wide[bin]++;
                    total++;
                    sum += v;
                }
            }
        }

        if (total === 0) {
            return null;
        }

        const lowCutoff = total * 0.02;
        const highCutoff = total * 0.995;
        const halfTotal = total / 2;
        let cum = 0;
        let lowBin = 0;
        let highBin = WIDE_BINS - 1;
        let medianBin = 0;
        let lowFound = false;
        let medianFound = false;
        let highFound = false;
        for (let i = 0; i < WIDE_BINS; i++) {
            cum += wide[i];
            if (!lowFound && cum >= lowCutoff) {
                lowBin = i;
                lowFound = true;
            }
            if (!medianFound && cum >= halfTotal) {
                medianBin = i;
                medianFound = true;
            }
            if (!highFound && cum >= highCutoff) {
                highBin = i;
                highFound = true;
            }
            if (lowFound && medianFound && highFound) {
                break;
            }
        }
        if (highBin <= lowBin) {
            highBin = Math.min(WIDE_BINS - 1, lowBin + 1);
        }

        const minValue = dataMin + (lowBin / WIDE_BINS) * dataSpan;
        const maxValue = dataMin + ((highBin + 1) / WIDE_BINS) * dataSpan;
        const sampleMean = sum / total;
        const sampleMedian =
            dataMin + ((medianBin + 0.5) / WIDE_BINS) * dataSpan;
        let voxelsBelowDisplay = 0;
        for (let i = 0; i < lowBin; i++) {
            voxelsBelowDisplay += wide[i];
        }
        let voxelsAboveDisplay = 0;
        for (let i = highBin + 1; i < WIDE_BINS; i++) {
            voxelsAboveDisplay += wide[i];
        }

        const visibleBins = highBin - lowBin + 1;
        const bins = new Array<number>(numBins).fill(0);
        if (visibleBins >= numBins) {
            // Source resolution is finer than the display: aggregate multiple
            // wide bins into each display bin.
            for (let i = 0; i < visibleBins; i++) {
                const displayIdx = Math.min(
                    numBins - 1,
                    Math.floor((i / visibleBins) * numBins)
                );
                bins[displayIdx] += wide[lowBin + i];
            }
        } else {
            // Source resolution is coarser than the display (low-dynamic-range
            // volumes). Distribute each wide bin's count across the display
            // bins it covers; otherwise the counts would land on widely-spaced
            // indices (e.g. 0, 12, 25, ...) and render as artificial spikes
            // separated by empty gaps.
            for (let i = 0; i < visibleBins; i++) {
                const startDisplay = Math.floor((i / visibleBins) * numBins);
                const endDisplay = Math.min(
                    numBins,
                    Math.floor(((i + 1) / visibleBins) * numBins)
                );
                const span = Math.max(1, endDisplay - startDisplay);
                const perBin = wide[lowBin + i] / span;
                for (let d = startDisplay; d < endDisplay; d++) {
                    bins[d] += perBin;
                }
            }
        }

        const result: IntensityHistogram = {
            bins,
            minValue,
            maxValue,
            dataMin,
            dataMax,
            sampleMean,
            sampleMedian,
            voxelsBelowDisplay,
            voxelsAboveDisplay,
            totalSampled: total,
        };
        return result;
    }

    /**
     * Find the patient-space coordinates of the voxel with maximum intensity.
     *
     * `null` while a progressive load is incomplete: the maximum of a
     * partially filled volume points at whichever slice happened to arrive,
     * and would jump around as loading proceeds.
     */
    public findMaxIntensityPosition(): ReadonlyVec3 | null {
        if (this.isDisplayStateFrozen) {
            return null;
        }
        const data = this.floatPixelData;
        if (data.length === 0) {
            return null;
        }

        let maxVal = -Infinity;
        let maxIdx = 0;

        for (let i = 0; i < data.length; i++) {
            if (data[i] > maxVal) {
                maxVal = data[i];
                maxIdx = i;
            }
        }

        return this.grid.indexToVoxelCenterWorldSpace(maxIdx);
    }

    public constructor(
        grid: Grid,
        dicomUid: string,
        forUid: string,
        public readonly firstSlice: DicomImageSlice, // TODO: remove this, needed now to construct the ImageObject,
        series: Series,
        interpolationInfo?: InterpolationInfo,
        id: ImageId = newImageId(),
        progressive?: ProgressiveInit
    ) {
        this.id = id;
        this._series = series;
        if (progressive !== undefined) {
            // Progressive path: the series holds only the anchor slice at this
            // point, so the MR median below would be a median of one — and it
            // must not be recomputed later either, or the contrast would move
            // under the user mid-load. Take the manifest's declared W/L, then
            // the anchor's own.
            this._acquisitionWindowLevel =
                progressive.manifest.acquisitionWindowLevel ??
                firstSlice.windowLevel;
        } else {
            // MR images often have different WC/WW per slice;
            // use median for robustness. Other modalities use first slice.
            this._acquisitionWindowLevel =
                firstSlice.modality === Modality.MR
                    ? this.computeMedianAcquisitionWindowLevel(series)
                    : firstSlice.windowLevel;
        }
        this._interpolationInfo = interpolationInfo;

        this.grid = grid;
        this.dicomUid = dicomUid;
        this.frameOfReferenceUid = forUid;
        this._pixelData = new Int16Array(0);

        if (progressive !== undefined) {
            const range = seededIntensityRange(firstSlice.modality);
            const sopUidByZ = new Array<string | undefined>(grid.sizeZ);
            for (const [sopUid, zIndex] of progressive.zIndexBySopUid) {
                sopUidByZ[zIndex] = sopUid;
            }
            this._progressive = {
                manifest: progressive.manifest,
                zIndexBySopUid: progressive.zIndexBySopUid,
                sopUidByZ,
                arrived: new Set<string>(),
                runningMinValue: Infinity,
                runningMaxValue: -Infinity,
                unavailable: new Set<string>(),
                seededMinValue: range[0],
                seededMaxValue: range[1],
                seededWindowLevel: seededWindowLevel(
                    this._acquisitionWindowLevel,
                    firstSlice.modality,
                    range
                ),
                completionCachesReset: false,
            };
            // Allocate the whole volume up front. The engine takes a reference
            // to `floatPixelData` once and uploads slabs into it as they land,
            // so the buffer identity must never change after construction.
            const voxelCount = grid.sizeX * grid.sizeY * grid.sizeZ;
            this._floatPixelData = new Float32Array(voxelCount);
            if (firstSlice.modality !== Modality.PT) {
                // PET fills the Float32 buffer directly and has no valid Int16
                // representation at all, so only CT/MR get an Int16 volume.
                this._pixelData = new Int16Array(voxelCount);
            }
        }
    }

    private readonly _progressive: ProgressiveLoadState | undefined;

    private readonly _series: Series;

    private readonly _acquisitionWindowLevel: WindowLevel | undefined;

    /**
     * Compute the acquisition window level as the median WC/WW
     * across all slices. MR images often have different WC/WW
     * values per slice; using the median is more robust than
     * picking the first slice.
     */
    private computeMedianAcquisitionWindowLevel(
        series: Series
    ): WindowLevel | undefined {
        const slices = series.dicomImageSlices;
        const windows: number[] = [];
        const levels: number[] = [];
        for (const slice of slices) {
            if (slice.windowLevel !== undefined) {
                windows.push(slice.windowLevel.window);
                levels.push(slice.windowLevel.level);
            }
        }
        if (windows.length === 0) {
            return undefined;
        }
        windows.sort((a, b) => a - b);
        levels.sort((a, b) => a - b);
        const mid = Math.floor(windows.length / 2);
        const medianWindow =
            windows.length % 2 === 1
                ? windows[mid]
                : Math.round((windows[mid - 1] + windows[mid]) / 2);
        const medianLevel =
            levels.length % 2 === 1
                ? levels[mid]
                : Math.round((levels[mid - 1] + levels[mid]) / 2);
        return new WindowLevel(Math.max(1, medianWindow), medianLevel);
    }

    private readonly _interpolationInfo: InterpolationInfo | undefined;

    private readonly classifiedSuvQuantification =
        new DerivedValue<SuvQuantification>(
            () =>
                classifySuvQuantification({
                    isPt: this.firstSlice.modality === Modality.PT,
                    units: this.firstSlice.units,
                    decayCorrection: this.firstSlice.decayCorrection,
                    suvType: this.firstSlice.suvType,
                    correctedImage: this.firstSlice.correctedImage,
                    petMetadata: this.petMetadata,
                    hasIntensityRange: this.minValue < this.maxValue,
                }),
            this.derivations
        );

    get trackableId(): ImageId {
        return this.id;
    }

    attachTracker(tracker: ChangeTracker): void {
        this.tracker = tracker;
    }

    captureSnapshot(): ImageSnapshot {
        const res = this.grid.getRes();
        return {
            id: this.id,
            seriesInstanceUid: this.seriesUid,
            frameOfReferenceUid: this.frameOfReferenceUid,
            modality: this.firstSlice.modality,
            sizeVoxels: [this.grid.sizeX, this.grid.sizeY, this.grid.sizeZ],
            // Copy out of the live vec — a snapshot must never alias
            // mutable domain state.
            resolutionMm: [res[0], res[1], res[2]],
            sliceCount: this.grid.sizeZ,
            isNew: this.isNew,
            isExported: this.isExported,
        };
    }

    public get series(): Series {
        return this._series;
    }

    public get generationModel(): GenerationModelDetails | undefined {
        const seriesObject = this.series.seriesObjects.find(
            (x) => x.generationModel
        );
        return seriesObject?.generationModel;
    }

    public get isNew(): boolean {
        return this.series.seriesObjects.some((x) => x.isNew);
    }

    public get isExported(): boolean {
        return this.series.seriesObjects.some((x) => x.isExported);
    }

    private _resamplingOrigin: ResamplingOrigin | undefined;
    private _resampledByRegistrationUids: readonly string[] = [];

    public get resamplingOrigin(): ResamplingOrigin | undefined {
        return this._resamplingOrigin;
    }

    public get isSpatiallyResampled(): boolean {
        return this._resamplingOrigin !== undefined;
    }

    public get resampledByRegistrationUids(): readonly string[] {
        return this._resampledByRegistrationUids;
    }

    public wasResampledBy(registrationSopInstanceUid: string): boolean {
        return this._resampledByRegistrationUids.includes(
            registrationSopInstanceUid
        );
    }

    public markAsSpatiallyResampled(
        origin: ResamplingOrigin,
        producedByRegistrationUids: readonly string[] = []
    ): void {
        // Provenance is never rewritten: the first origin and registrations hold.
        if (this._resamplingOrigin !== undefined) {
            return;
        }
        this._resamplingOrigin = origin;
        this._resampledByRegistrationUids = [...producedByRegistrationUids];
    }

    public setExported(): void {
        // Mark before mutating — see DoseMatrix.setExported. Worst case in
        // the family: series.setExported() flips every object in the series,
        // so a throw after it would leave the whole series exported with no
        // changeset.
        this.tracker?.markChanged(this);
        this.series.setExported();
    }

    /**
     * Adopts a display label onto the in-memory series metadata AND every
     * slice dataset (so a later export re-serializes the adopted name).
     * Load-time overlay only — no tracker mark. Used by hydration when the
     * database row's SeriesDescription is authoritative after a rename;
     * the DB's stored files keep their original tag by design.
     */
    public adoptSeriesDescription(seriesDescription: string): void {
        (
            this.series.seriesModule as { seriesDescription: string }
        ).seriesDescription = seriesDescription;
        for (const slice of this.series.dicomImageSlices) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (slice.datasetWrapper.dataset as any).SeriesDescription =
                seriesDescription;
        }
    }

    /**
     * Renames the series as a tracked user edit: same mutation as
     * {@link adoptSeriesDescription} plus the changeset mark, so the
     * pending-changes store classifies the rename for the save workflow.
     */
    public rename(seriesDescription: string): void {
        // Mark before mutating (family convention, see setExported).
        this.tracker?.markChanged(this);
        this.adoptSeriesDescription(seriesDescription);
    }

    private readonly measuredMaxValue = new DerivedValue<number>(() => {
        const accumulated = this.accumulatedIntensityRange;
        if (accumulated !== undefined) {
            return accumulated.max;
        }
        let max = -Infinity;
        for (const d of this.floatPixelData) {
            if (d > max) {
                max = d;
            }
        }
        return max;
    }, this.derivations);

    /**
     * Highest voxel value in the volume — or, while a progressive load is
     * incomplete, the frozen modality seed (see {@link seededIntensityRange}).
     * {@link hasMeasuredIntensityRange} tells the two apart.
     */
    public get maxValue(): number {
        if (this.isDisplayStateFrozen) {
            return this._progressive!.seededMaxValue;
        }
        return this.measuredMaxValue.get();
    }

    /**
     * The range accumulated slice by slice, but only once it describes the
     * whole volume: until every slice has arrived it spans a subset of the
     * rows, and the buffer's untouched rows are zeros it has never seen.
     *
     * `undefined` for a parsed series, which was whole before this class saw
     * it and so accumulated nothing — that one is scanned.
     */
    private get accumulatedIntensityRange():
        | { readonly min: number; readonly max: number }
        | undefined {
        if (this._progressive === undefined || !this.isComplete) {
            return undefined;
        }
        return {
            min: this._progressive.runningMinValue,
            max: this._progressive.runningMaxValue,
        };
    }

    private readonly measuredMinValue = new DerivedValue<number>(() => {
        const accumulated = this.accumulatedIntensityRange;
        if (accumulated !== undefined) {
            return accumulated.min;
        }
        let min = Infinity;
        for (const d of this.floatPixelData) {
            if (d < min) {
                min = d;
            }
        }
        return min;
    }, this.derivations);

    /**
     * Lowest voxel value in the volume — or, while a progressive load is
     * incomplete, the frozen modality seed (see {@link seededIntensityRange}).
     *
     * This one is load-bearing beyond display: it bounds the intensity range
     * the drawing constraints threshold within, so a value that tracked the
     * arriving slices would move the bounds under a clinician mid-operation.
     * {@link hasMeasuredIntensityRange} tells seed from measurement.
     */
    public get minValue(): number {
        if (this.isDisplayStateFrozen) {
            return this._progressive!.seededMinValue;
        }
        return this.measuredMinValue.get();
    }

    /**
     * Whether this image's voxel values can be interpreted as Hounsfield
     * Units. True only for a CT whose (optional) `RescaleType` does not
     * declare a non-HU output unit and whose intensity range is
     * non-degenerate. An absent `RescaleType` counts as HU — the CT IOD
     * defines HU output and most series omit the tag — but an explicit
     * other value (e.g. `US` on some derived/localizer series) makes the
     * HU interpretation wrong, so it is rejected rather than assumed.
     * Gate on this before any operation that thresholds in HU (body
     * contour extraction); it does NOT guarantee calibration accuracy
     * (a scatter-shifted CBCT still yields `true` — the user compensates
     * with the threshold, not by an eligibility branch).
     */
    public get isHuQuantified(): boolean {
        if (this.firstSlice.modality !== Modality.CT) {
            return false;
        }
        const rescaleType = this.firstSlice.rescaleType.trim().toUpperCase();
        if (rescaleType !== '' && rescaleType !== 'HU') {
            return false;
        }
        return this.minValue < this.maxValue;
    }

    /**
     * The single durable record of this volume's SUV quantification state
     * — see {@link classifySuvQuantification} for the rules. Classified
     * once (lazily: it needs the volume min/max scan) and memoized;
     * {@link isSuvQuantified} and {@link suvFactor} are views on it, so
     * every consumer of either inherits the same verdict — the factor
     * cannot exist for a series whose Units/DecayCorrection make
     * `raw × factor` wrong (the double-conversion hazard on pre-converted
     * GML exports, and the mislabeled kBq/ml fallback on CNTS).
     */
    public get suvQuantification(): SuvQuantification {
        return this.classifiedSuvQuantification.get();
    }

    /**
     * The PET conversion inputs for THIS VOLUME, with the scan datetime
     * resolved series-wide: the minimum acquisition datetime across ALL
     * slices (mvdicom ct_mr.py:1310-1318), then the earlier-of against the
     * series datetime (the QIBA rule). The slice-level resolver cannot do
     * this — it sees one slice, `dicomImageSlices[0]` is file-arrival
     * order (nondeterministic between sessions), and on multi-bed PET an
     * arbitrary bed's acquisition time can be many minutes after the scan
     * start, silently inflating the decay interval and the factor. Feed
     * THIS record (not `firstSlice.petMetadata`) to anything that computes
     * or displays the volume's SUV timing.
     */
    public get petMetadata(): PetSeriesMetadata | null {
        const slicePetMetadata = this.firstSlice.petMetadata;
        if (slicePetMetadata === null) {
            return null;
        }
        return {
            ...slicePetMetadata,
            scanDateTime: this.resolveSeriesScanDateTime(),
        };
    }

    /**
     * Series-wide scan datetime: min acquisition datetime over all slices,
     * then earlier-of against the series datetime. Order-invariant by
     * construction (a minimum), which is what makes the SUV factor
     * deterministic across load orders.
     */
    private resolveSeriesScanDateTime(): string | undefined {
        let earliestAcquisition: string | undefined;
        for (const slice of this._series.dicomImageSlices) {
            // earlierDicomDateTime skips absent/malformed candidates, so a
            // single bad slice cannot poison the minimum.
            earliestAcquisition = earlierDicomDateTime(
                earliestAcquisition,
                slice.resolvedAcquisitionDateTime
            );
        }
        return earlierDicomDateTime(
            this.firstSlice.resolvedSeriesDateTime,
            earliestAcquisition
        );
    }

    /**
     * Whether this image's voxel values can be read as SUVbw — the PET
     * analogue of {@link isHuQuantified}. True for a BQML series
     * decay-corrected to scan start with a computable factor, AND for a
     * vendor pre-converted GML series whose SUVType is BW or absent
     * (factor 1). STRICT for everything else: an absent or other
     * Units/DecayCorrection rejects rather than assumes — `raw × factor`
     * on a CNTS or non-START series would present plausible-looking
     * wrong SUV numbers as valid. Gate on this before any operation that
     * thresholds or reports in SUVbw; it does NOT guarantee quantitative
     * calibration accuracy (scanner QC is the user's domain).
     */
    public get isSuvQuantified(): boolean {
        const state = this.suvQuantification.state;
        return state === 'bqml' || state === 'preconverted-gml';
    }

    /**
     * raw → SUVbw multiplier, or null when the volume is not
     * SUVbw-quantified. A view on {@link suvQuantification}: this is
     * deliberately the SAME verdict the strict gate uses, so every
     * display consumer (readout, W/L, colorwash, drawing thresholds)
     * inherits it — a GML/CNTS or non-START series gets no factor and
     * therefore no SUV-labeled numbers.
     */
    public get suvFactor(): number | null {
        return this.suvQuantification.factor;
    }

    public get patientWeightKg(): number | undefined {
        return this.firstSlice.petMetadata?.patientWeightKg;
    }

    public readonly grid: Grid;

    public readonly dicomUid: string;

    public readonly frameOfReferenceUid: string;

    private _windowLevel: WindowLevel | undefined;

    /** Whether the user has chosen a window: see {@link setWindowLevel}. */
    private _isWindowLevelSetByUser = false;

    public get windowLevel(): WindowLevel | undefined {
        if (!this._windowLevel) {
            return undefined;
        }
        return this._windowLevel;
    }

    /**
     * Sets the window/level for this image. A `source` of 'user' records
     * that the user has chosen a window for it (a typed value, a preset, a
     * Reset to an acquired window), which
     * {@link windowLevelForFirstDisplay} keeps at the completion of a
     * progressive load. A 'user' window equal to the full range of the
     * load's placeholder, such as a Reset while a PET with no acquired
     * window loads, clears that record. Any other source leaves it as it
     * is.
     *
     * **Deliberately not tracked.** Window/level is transient display
     * state written from the engine's interaction path (see
     * `Environment.applyWindowLevel` / `ImageObject`) on every mouse-drag
     * frame. Emitting a changeset per frame would flood the persistence
     * log and would force a `tracker.command()` around the render loop.
     * It is persisted onto the first slice so the setting survives
     * workflow switches, which is the only durability it needs.
     */
    public setWindowLevel(value: WindowLevel, source: EventSource): void {
        if (source === 'user') {
            const placeholder =
                this._progressive &&
                fullRangeWindowLevel(this._progressive.seededMaxValue);
            this._isWindowLevelSetByUser = !placeholder?.equals(value);
        }
        this._windowLevel = value;
        this.firstSlice.windowLevel = value;
    }

    private _pixelData: Int16Array;

    /**
     * Int16 pixel data for the full volume.
     * Not available for PET images — use {@link floatPixelData} instead.
     *
     * **Throws while a progressive load is incomplete.** Its consumers —
     * elastix registration, reformat, body-contour extraction — are all
     * whole-volume algorithms, and a volume whose not-yet-arrived slices are
     * still zeros would produce a plausible-looking wrong answer with no
     * symptom. A loud failure is the conservative choice; callers gate on
     * {@link isComplete} first.
     */
    public get pixelData(): Int16Array {
        if (this.hasWidePixelRange) {
            throw new Error(
                'Int16 pixel data is not available for PET images — ' +
                    'rescaled values exceed Int16 range. ' +
                    'Use floatPixelData.'
            );
        }
        if (!this.isComplete) {
            throw new DomainValidationError(
                'Int16 pixel data is not available while the image is still ' +
                    `loading (${this.loadedSliceCount} of ${this.expectedSliceCount} ` +
                    `slices loaded, ${this.pendingSliceCount} pending, ` +
                    `${this.unavailableSliceCount} unavailable). ` +
                    'Whole-volume operations must gate on Image3D.isComplete.'
            );
        }
        if (this._pixelData.length === 0) {
            this._pixelData = new Int16Array(
                this.grid.sizeX * this.grid.sizeY * this.grid.sizeZ
            );
            if (this._interpolationInfo) {
                this.updatePixelDataWithInterpolation();
            } else {
                this.updatePixelDataFromSeries();
            }
        }
        return this._pixelData;
    }

    private _floatPixelData: Float32Array | null = null;

    /**
     * Float32 representation of the pixel data, suitable for GPU upload
     * and value lookups.
     *
     * - PET: assembled directly from DICOM slice Float32 data — no Int16
     *   intermediate, no overflow/correction needed.
     * - CT/MR: widened from the Int16 volume data.
     *
     * Built lazily on first access and cached for the lifetime of the
     * Image3D instance. ImageObject takes a reference to this array
     * rather than allocating its own copy.
     *
     * **Progressive contract.** For an image built by {@link fromManifest}
     * this is a full-size `Float32Array` allocated at construction, and it is
     * returned even while the load is incomplete: the rows belonging to slices
     * reported by {@link isSliceLoaded} hold real pixels, and the rows of
     * pending or unavailable slices are still zero. The array identity never
     * changes, so the engine may take the reference once and upload arriving
     * slabs out of it. Anything that reduces over the WHOLE volume must gate
     * on {@link isComplete} — {@link pixelData} enforces that; this getter
     * deliberately does not, because the engine needs the partial buffer.
     */
    public get floatPixelData(): Float32Array {
        if (this._floatPixelData !== null) {
            return this._floatPixelData;
        }

        if (this.hasWidePixelRange) {
            // PET: assemble Float32 directly from DICOM — no Int16
            // intermediate
            if (this._interpolationInfo) {
                throw new Error(
                    'Slice interpolation is not supported ' + 'for PET images.'
                );
            }
            this.updateFloatPixelDataFromSeries();
            return this._floatPixelData!;
        }

        // CT/MR: widen from Int16
        const int16Data = this.pixelData;
        const float32 = new Float32Array(int16Data.length);
        for (let i = 0; i < int16Data.length; i++) {
            float32[i] = int16Data[i];
        }
        this._floatPixelData = float32;
        return this._floatPixelData;
    }

    /**
     * Whether the rescaled pixel values can exceed the Int16 range.
     * True for PET images where per-slice RescaleSlope causes values
     * above 32767 to wrap into negative Int16 territory.
     */
    public get hasWidePixelRange(): boolean {
        return this.firstSlice.modality === Modality.PT;
    }

    /** Whether this image contains interpolated slices */
    public get hasInterpolatedSlices(): boolean {
        return this._interpolationInfo !== undefined;
    }

    /** Number of slices that were interpolated (0 if no interpolation) */
    public get interpolatedSliceCount(): number {
        if (!this._interpolationInfo) {
            return 0;
        }
        return (
            this._interpolationInfo.totalSlices -
            this._interpolationInfo.sliceMap.size
        );
    }

    /**
     * Whether the slice at `sliceIndex` along the grid's slice axis (z) was
     * interpolated from its neighbours, so holds no acquired data. Throws
     * a RangeError for an index that names no slice of the image.
     */
    public isSliceInterpolated(sliceIndex: number): boolean {
        if (
            !Number.isInteger(sliceIndex) ||
            sliceIndex < 0 ||
            sliceIndex >= this.grid.sizeZ
        ) {
            throw new RangeError(
                `Slice index ${sliceIndex} is outside 0..${this.grid.sizeZ - 1}`
            );
        }
        return (
            this._interpolationInfo !== undefined &&
            !this._interpolationInfo.sliceMap.has(sliceIndex)
        );
    }

    /**
     * Number of DICOM instances the series holds: every slice of the volume
     * except the interpolated ones. Known from the manifest before the
     * slices arrive, so it does not move while the image streams.
     */
    public get instanceCount(): number {
        return this.grid.sizeZ - this.interpolatedSliceCount;
    }

    // -----------------------------------------------------------------
    // Progressive loading (manifest-driven database path)
    //
    // An image built by fromSeries is complete the moment it exists, so
    // every accessor below answers "complete, nothing pending" for it and
    // no existing caller has to learn about progressive state.
    // -----------------------------------------------------------------

    /** Whether this image was built from a manifest and loads progressively. */
    public get isProgressivelyLoaded(): boolean {
        return this._progressive !== undefined;
    }

    /** Total number of slices this volume holds. */
    public get expectedSliceCount(): number {
        return this.grid.sizeZ;
    }

    /** Slices whose pixels are present in the buffers. */
    public get loadedSliceCount(): number {
        return this._progressive?.arrived.size ?? this.grid.sizeZ;
    }

    /**
     * Slices whose retrieval failed terminally. They stay missing: a failed
     * slice is NEVER turned into interpolated data, because "synthesized
     * anatomy" and "never arrived" must not be the same state on screen.
     */
    public get unavailableSliceCount(): number {
        return this._progressive?.unavailable.size ?? 0;
    }

    /** Slices still expected to arrive. */
    public get pendingSliceCount(): number {
        if (this._progressive === undefined) {
            return 0;
        }
        return (
            this.expectedSliceCount -
            this._progressive.arrived.size -
            this._progressive.unavailable.size
        );
    }

    /**
     * Whether the slice at this z-index holds retrieved pixels.
     *
     * The buffers are allocated whole and filled per slice, so an unarrived
     * row reads back as the zero it was allocated with — indistinguishable, in
     * the data alone, from a measured value. Anything that reports a voxel to
     * the user has to ask this first; a probe of a pending row is "no value
     * yet", not "0".
     *
     * Always true for a parsed series, whose buffers were whole on arrival.
     */
    public hasArrivedAtZ(zIndex: number): boolean {
        const state = this._progressive;
        if (state === undefined) {
            return true;
        }
        if (
            !Number.isInteger(zIndex) ||
            zIndex < 0 ||
            zIndex >= this.grid.sizeZ
        ) {
            return false;
        }
        const sopUid = state.sopUidByZ[zIndex];
        return sopUid !== undefined && state.arrived.has(sopUid);
    }

    /** Every slice's pixels are present. Always true for a parsed series. */
    public get isComplete(): boolean {
        return (
            this._progressive === undefined ||
            this._progressive.arrived.size === this.expectedSliceCount
        );
    }

    /**
     * Terminal failure state: retrieval has finished (nothing is pending) but
     * the volume is still missing slices. Distinct from "still loading" and
     * distinct from interpolation — this image will never become complete and
     * whole-volume operations on it must stay blocked.
     */
    public get isIncompleteFinal(): boolean {
        return !this.isComplete && this.pendingSliceCount === 0;
    }

    /**
     * Whether {@link minValue} / {@link maxValue} report the volume's measured
     * intensity range. `false` while a progressive load is incomplete, where
     * they report the frozen modality seed instead — gate on this before
     * treating the range as a measurement.
     */
    public get hasMeasuredIntensityRange(): boolean {
        return this.isComplete;
    }

    /**
     * SOP Instance UIDs still awaiting retrieval, in manifest (z ascending)
     * order. Empty for a parsed series.
     */
    public get pendingSliceSopUids(): readonly string[] {
        const state = this._progressive;
        if (state === undefined) {
            return [];
        }
        return state.manifest.slices
            .map((slice) => slice.sopInstanceUid)
            .filter(
                (uid) => !state.arrived.has(uid) && !state.unavailable.has(uid)
            );
    }

    /**
     * Z rows whose slice will never arrive, ascending. Empty for a parsed
     * series and for one that is merely still loading — a row is listed here
     * only once retrieval has given up on it.
     */
    public get unavailableSliceZIndices(): readonly number[] {
        const state = this._progressive;
        if (state === undefined) {
            return [];
        }
        const zIndices: number[] = [];
        for (const sopUid of state.unavailable) {
            const zIndex = state.zIndexBySopUid.get(sopUid);
            if (zIndex !== undefined) {
                zIndices.push(zIndex);
            }
        }
        return zIndices.sort((a, b) => a - b);
    }

    /** Whether the given slice's pixels are already in the volume. */
    public isSliceLoaded(sopInstanceUid: string): boolean {
        const state = this._progressive;
        if (state === undefined) {
            return this.hasSlice(sopInstanceUid);
        }
        return state.arrived.has(sopInstanceUid);
    }

    /**
     * Write one retrieved slice's pixels into the pre-allocated volume and
     * record its arrival.
     *
     * Placement is derived from the slice's own Image Position Patient through
     * the volume's grid — the same arithmetic
     * {@link updatePixelDataFromSeries} uses — and is cross-checked against the
     * manifest, so a slice whose pixels and whose database row disagree about
     * where it belongs is rejected rather than written to the wrong depth.
     *
     * Each slice writes only its own slab, so the finished buffer is identical
     * regardless of arrival order.
     *
     * Nothing is mutated until every check has passed and the slice's pixels
     * have decoded, so a rejected slice leaves the volume and the accounting
     * untouched.
     *
     * @param slice A parsed slice belonging to this image's manifest
     * @returns The z-index of the written slab (the engine uploads that slab)
     * @throws DomainValidationError if the slice is unknown to the manifest,
     *   already inserted, already marked unavailable, wrongly sized, or
     *   positioned outside the volume
     */
    public insertSlice(slice: DicomImageSlice): number {
        const state = this._progressive;
        if (state === undefined) {
            throw new DomainValidationError(
                'insertSlice is only valid on an image built with Image3D.fromManifest'
            );
        }

        const sopUid = slice.sopInstanceUid;
        const manifestZIndex = state.zIndexBySopUid.get(sopUid);
        if (manifestZIndex === undefined) {
            // No SOP UID in the message: these reach logs and toasts.
            throw new DomainValidationError(
                'Slice is not part of this image series manifest'
            );
        }
        if (state.arrived.has(sopUid)) {
            throw new DomainValidationError(
                'Slice has already been inserted into this image'
            );
        }
        if (state.unavailable.has(sopUid)) {
            throw new DomainValidationError(
                'Slice was marked unavailable and cannot be inserted'
            );
        }
        if (
            slice.rows !== state.manifest.rows ||
            slice.cols !== state.manifest.columns
        ) {
            throw new DomainValidationError(
                `Slice is ${slice.cols}x${slice.rows}, but the image series ` +
                    `manifest declares ${state.manifest.columns}x${state.manifest.rows}`
            );
        }

        const zIndex = Math.floor(
            this.grid.toVoxelSpace(slice.imagePosition)[2]
        );
        if (zIndex < 0 || zIndex >= this.grid.sizeZ) {
            throw new DomainValidationError(
                `Slice position outside of image volume (z ${zIndex} of ` +
                    `${this.grid.sizeZ} slices)`
            );
        }
        if (zIndex !== manifestZIndex) {
            throw new DomainValidationError(
                `Slice geometry disagrees with the image series manifest: ` +
                    `pixels place it at z-index ${zIndex}, the manifest at ${manifestZIndex}`
            );
        }

        const sliceSize = this.grid.sizeX * this.grid.sizeY;
        // Decode before mutating: a slice that fails to decode must not leave
        // the volume half-written or the accounting inconsistent.
        //
        // PET fills Float32 directly — its rescaled values leave Int16 range,
        // so no Int16 intermediate exists for it. Everything else takes the
        // Int16 route and WIDENS from it, which is not the same number as the
        // slice's own Float32: `dicomSlicePixelDataToInternal` truncates to
        // Int16 and wraps unsigned values. A series assembled by the parser
        // widens from that same Int16 volume, so writing full-precision floats
        // here would render the identical series differently depending on
        // whether it happened to stream.
        const int16Pixels = this.hasWidePixelRange ? null : slice.pixelData;
        const floatPixels = int16Pixels === null ? slice.floatPixelData : null;
        const decodedLength = (int16Pixels ?? floatPixels!).length;
        if (decodedLength !== sliceSize) {
            throw new DomainValidationError(
                `Slice carries ${decodedLength} voxels, expected ${sliceSize}`
            );
        }

        // The intensity range is accumulated here, while the slice's voxels are
        // being walked anyway, rather than scanned out of the whole buffer once
        // the load finishes. The scan is the volume's largest read, and the
        // moment it would run — the last slice landing — is the moment the user
        // starts working.
        const offset = zIndex * sliceSize;
        let sliceMin = Infinity;
        let sliceMax = -Infinity;
        if (int16Pixels !== null) {
            this._pixelData.set(int16Pixels, offset);
            for (let i = 0; i < sliceSize; i++) {
                const value = int16Pixels[i];
                this._floatPixelData![offset + i] = value;
                if (value < sliceMin) {
                    sliceMin = value;
                }
                if (value > sliceMax) {
                    sliceMax = value;
                }
            }
        } else {
            this._floatPixelData!.set(floatPixels!, offset);
            for (let i = 0; i < sliceSize; i++) {
                const value = floatPixels![i];
                if (value < sliceMin) {
                    sliceMin = value;
                }
                if (value > sliceMax) {
                    sliceMax = value;
                }
            }
        }
        if (sliceMin < state.runningMinValue) {
            state.runningMinValue = sliceMin;
        }
        if (sliceMax > state.runningMaxValue) {
            state.runningMaxValue = sliceMax;
        }
        this.series.addSeriesObject(slice);
        state.arrived.add(sopUid);

        if (this.isComplete && !state.completionCachesReset) {
            state.completionCachesReset = true;
            this.resetDerivedValues();
        }

        return zIndex;
    }

    /**
     * Record that a slice will never arrive (retrieval failed after retries).
     *
     * The image stays explicitly incomplete — see {@link isIncompleteFinal}.
     * Idempotent, so an exhausted fetcher may call it more than once.
     *
     * @returns The z row the slice would have occupied, so a caller can show
     * that row as absent without repeating the manifest lookup.
     * @throws DomainValidationError if the slice is unknown to the manifest or
     *   has already been inserted
     */
    public markSliceUnavailable(sopInstanceUid: string): number {
        const state = this._progressive;
        if (state === undefined) {
            throw new DomainValidationError(
                'markSliceUnavailable is only valid on an image built with Image3D.fromManifest'
            );
        }
        const zIndex = state.zIndexBySopUid.get(sopInstanceUid);
        if (zIndex === undefined) {
            throw new DomainValidationError(
                'Slice is not part of this image series manifest'
            );
        }
        if (state.arrived.has(sopInstanceUid)) {
            throw new DomainValidationError(
                'Slice has already been inserted and cannot be marked unavailable'
            );
        }
        state.unavailable.add(sopInstanceUid);
        return zIndex;
    }

    /**
     * Whether whole-volume-derived display state is held at its seeded value.
     * True exactly while a progressive load is still incomplete.
     */
    private get isDisplayStateFrozen(): boolean {
        return this._progressive !== undefined && !this.isComplete;
    }

    /**
     * Drop every value derived from the whole volume, once, when the last
     * pending slice arrives, so the NEXT read derives it over the finished
     * volume.
     *
     * All of them, not a chosen few: while the volume was incomplete, a
     * derivation either read the frozen seed rather than a measurement or
     * reduced over rows that were still zero, so none of their results
     * describe the volume that now exists. `suvQuantification` is the one
     * whose dependency is indirect — it is classified from
     * `hasIntensityRange`, a comparison of the min and max — and it is the
     * one a hand-written list of fields has already been observed to miss,
     * which is why the values register themselves instead.
     *
     * Deliberately a reset and not a recompute: nothing is pushed at anyone.
     * The user-set and acquisition window/levels are left alone, so the image
     * on screen does not change contrast at the moment the load finishes —
     * a user who wants the full-volume auto window asks for it. The one
     * window a display replaces then is a PET window that still carries the
     * level bounds of the seeded placeholder: see
     * {@link windowLevelForFirstDisplay}.
     */
    private resetDerivedValues(): void {
        for (const derivation of this.derivations) {
            derivation.reset();
        }
    }

    /**
     * Update pixel data with interpolation for missing slices
     */
    private updatePixelDataWithInterpolation(): void {
        const info = this._interpolationInfo!;
        const sliceSize = this.grid.sizeX * this.grid.sizeY;

        // Build SliceInfo array for interpolation lookups
        const slices: SliceInfo[] = [];
        for (const [zIndex, slice] of info.sliceMap) {
            slices.push({ zIndex, pixelData: slice.pixelData });
        }

        // Fill all slices
        for (let z = 0; z < info.totalSlices; z++) {
            const pixelIdx = z * sliceSize;

            if (info.sliceMap.has(z)) {
                // Real slice - copy data directly
                this._pixelData.set(info.sliceMap.get(z)!.pixelData, pixelIdx);
            } else {
                // Find nearest slices for interpolation
                const [below, above] = findNearestSlices(z, slices);
                if (above.zIndex === below.zIndex) {
                    throw new Error(
                        'Cannot interpolate between identical z-indices'
                    );
                }
                // Missing slice - interpolate from neighbors
                const weight =
                    (z - below.zIndex) / (above.zIndex - below.zIndex);

                const interpolated = interpolateSlice(
                    below.pixelData,
                    above.pixelData,
                    weight,
                    info.interpolationMethod
                );
                this._pixelData.set(interpolated, pixelIdx);
            }
        }
    }

    public get seriesUid(): string {
        return this.series.seriesInstanceUID;
    }

    public get studyUid(): string {
        return this.firstSlice.studyInstanceUID;
    }

    public get length(): number {
        return this.grid.sizeZ;
    }

    public get seriesMetadata(): DicomSeries {
        return this.series.seriesModule;
    }

    public get sopClassUid(): string {
        return this.firstSlice.datasetWrapper.sopClassUID;
    }

    public get imageSlices(): DicomImageSlice[] {
        return this.series.dicomImageSlices;
    }

    private readonly aggregatedBurnedInAnnotation =
        new DerivedValue<BurnedInAnnotationStatus>(
            () =>
                aggregateBurnedInAnnotation(
                    this.imageSlices.map((slice) =>
                        parseBurnedInAnnotation(slice.burnedInAnnotation)
                    )
                ),
            this.derivations
        );

    /**
     * Fail-safe {@link BurnedInAnnotationStatus} for this image, aggregated over
     * all its slices (any slice YES → Yes; any Unknown → Unknown; only all-NO →
     * No). Consulted before treating the image's pixels as safe to export
     * (e.g. the viewport screenshot), since burned-in identifiers live in the
     * pixels and survive metadata de-identification.
     *
     * Cached: the per-slice scan is O(slices) yet this getter is read on the render
     * hot path, and the result derives only from immutable per-slice DICOM tags —
     * so it is memoized once like {@link maxValue} / {@link minValue}.
     *
     * While a progressive load is incomplete the answer is
     * {@link BurnedInAnnotationStatus.Unknown} — the fail-safe direction. The
     * slices that have arrived cannot vouch for the ones that have not, and
     * aggregating over the arrived subset alone could report the whole image
     * "No" (safe to export) and then flip to "Yes" after a screenshot has
     * already been taken. Unknown withholds the guarantee instead, which is
     * what the screenshot gate already treats as "awaiting review".
     */
    public get burnedInAnnotationStatus(): BurnedInAnnotationStatus {
        if (this.isDisplayStateFrozen) {
            return BurnedInAnnotationStatus.Unknown;
        }
        return this.aggregatedBurnedInAnnotation.get();
    }

    /**
     * Returns an iterator to get the slices as DICOM blobs.
     *
     * **Throws while a progressive load is incomplete**, for the same reason
     * {@link pixelData} does, and this one is the sharper hazard of the two.
     * Every consumer — the backend task hooks (auto-segmentation, contour
     * propagation, dose prediction, image translation, composite CBCT, data
     * upload), the connector image cache, and DICOM export/download — treats
     * what this yields as *the series*. On an incomplete image it would
     * instead yield only the slices that happened to have arrived: a
     * truncated volume, self-consistent in its own file count, that a model
     * would happily return contours or a dose for. Nothing about the result
     * would look wrong.
     *
     * The UI gates every one of those actions on completeness
     * (`useImageCompletenessGate`); this is the defence behind that gate, at
     * the one place all of them funnel through. Callers that legitimately
     * want a partial volume do not exist — if one ever does, it needs its own
     * explicitly-named accessor, not a relaxation here.
     *
     * @throws DomainValidationError if the image was spatially resampled, or
     * is not yet complete.
     */
    public *getSlicesAsDicomBlobs(): IterableIterator<Blob> {
        if (this._resamplingOrigin) {
            throw new DomainValidationError(
                `${resamplingRefusal(this._resamplingOrigin)} Callers must ` +
                    'gate on Image3D.isSpatiallyResampled.'
            );
        }
        if (!this.isComplete) {
            throw new DomainValidationError(
                'The image slices cannot be exported while the image is ' +
                    `still loading (${this.loadedSliceCount} of ` +
                    `${this.expectedSliceCount} slices loaded, ` +
                    `${this.pendingSliceCount} pending, ` +
                    `${this.unavailableSliceCount} unavailable). ` +
                    'Sending a truncated series to a backend model or an ' +
                    'export would silently misrepresent the patient. ' +
                    'Callers must gate on Image3D.isComplete.'
            );
        }
        for (const slice of this.series.dicomImageSlices) {
            yield slice.getAsDicomBlob();
        }
    }
}

/**
 * Cheapest readable array of rescaled intensities, for CPU value lookups.
 * PET (wide range) must read the Float32 volume — {@link Image3D.pixelData}
 * throws for it; everything else reads the existing Int16 volume, avoiding
 * the full-volume Float32 copy `floatPixelData` would allocate. Either lazy
 * getter can throw for an unbuildable volume; callers catch and degrade.
 */
export function imageIntensityPixels(
    image: Pick<Image3D, 'hasWidePixelRange' | 'pixelData' | 'floatPixelData'>
): Int16Array | Float32Array {
    return image.hasWidePixelRange ? image.floatPixelData : image.pixelData;
}
